import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type WOStatus = "OPEN" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "OVERDUE";
type WOType = "work-order" | "preventive" | "part-order" | "energy" | "documentation";
type ViewMode = "list" | "table" | "board" | "map";

interface WorkOrder {
  id: string;
  number: string;
  title: string;
  type: WOType;
  company: string;
  assetName: string;
  assetId: string;
  status: WOStatus;
  assignees: { initials: string }[];
  daysLeft?: number;
  scheduledDate?: string;
  notificationCount?: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_WORK_ORDERS: WorkOrder[] = [
  {
    id: "1", number: "WBRW10321", title: "Asset Repair & Maintenance",
    type: "work-order", company: "Acme International", assetName: "CNC Machine A", assetId: "M-2152",
    status: "OPEN", assignees: [{ initials: "MZ" }], daysLeft: 10, notificationCount: 2,
  },
  {
    id: "2", number: "WBRW10322", title: "Facility Maintenance Task",
    type: "preventive", company: "Beta Solutions", assetName: "HVAC Unit 3", assetId: "M-3041",
    status: "IN_PROGRESS", assignees: [{ initials: "MZ" }, { initials: "SP" }, { initials: "BP" }, { initials: "+2" }],
    notificationCount: 2,
  },
  {
    id: "3", number: "WBRW10323", title: "Machinery Repair Order",
    type: "part-order", company: "Gamma Enterprises", assetName: "Lathe Machine B", assetId: "M-1987",
    status: "OPEN", assignees: [], notificationCount: 2,
  },
  {
    id: "4", number: "WBRW10324", title: "Equipment Overhaul Request",
    type: "work-order", company: "Delta Corp", assetName: "Press Machine 2", assetId: "M-2152",
    status: "OPEN", assignees: [{ initials: "MZ" }, { initials: "SP" }, { initials: "BP" }, { initials: "+2" }],
    scheduledDate: "20/10/2025", notificationCount: 2,
  },
  {
    id: "5", number: "WBRW10325", title: "Infrastructure Upgrade Request",
    type: "energy", company: "Epsilon Industries", assetName: "Generator Unit", assetId: "M-2152",
    status: "IN_PROGRESS", assignees: [{ initials: "MZ" }], scheduledDate: "20/10/2025", notificationCount: 2,
  },
  {
    id: "6", number: "WBRW10326", title: "Vehicle Service Order",
    type: "documentation", company: "Zeta Manufacturing", assetName: "Forklift #5", assetId: "M-2152",
    status: "OVERDUE", assignees: [{ initials: "MZ" }], daysLeft: 10, scheduledDate: "20/10/2025", notificationCount: 2,
  },
  {
    id: "7", number: "WBRW10327", title: "System Calibration Request",
    type: "work-order", company: "Acme International", assetName: "Sensor Array X", assetId: "M-2152",
    status: "COMPLETED", assignees: [{ initials: "MZ" }], daysLeft: 10, scheduledDate: "20/10/2025", notificationCount: 2,
  },
];

// ─── Status Config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<WOStatus, { bg: string; color: string; label: string }> = {
  OPEN:        { bg: "#ccefeb", color: "#00b09b", label: "OPEN" },
  IN_PROGRESS: { bg: "#fff0e6", color: "#ff7a00", label: "IN PROGRESS" },
  COMPLETED:   { bg: "#e6f9f1", color: "#10b981", label: "COMPLETED" },
  ON_HOLD:     { bg: "#f3f4f6", color: "#748094", label: "ON HOLD" },
  OVERDUE:     { bg: "#fde5f0", color: "#ee0064", label: "OVERDUE" },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const WO_TYPE_CONFIG: Record<WOType, { bg: string; icon: React.ReactNode }> = {
  "work-order": {
    bg: "#e6e8fe",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#6974fb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="16" height="14" rx="2" />
        <path d="M7 5V4a2 2 0 014 0v1M8 11h6M8 14h4" />
      </svg>
    ),
  },
  "preventive": {
    bg: "#e0faf6",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#00b09b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M11 7v4l3 3" />
      </svg>
    ),
  },
  "part-order": {
    bg: "#fff0e6",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#ff7a00" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3l5 5-9 9-5 1 1-5 9-9z" />
        <path d="M12 5l5 5" />
      </svg>
    ),
  },
  "energy": {
    bg: "#e0f8e8",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#10b981" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L6 13h7l-1 7 8-11h-7l2-7z" />
      </svg>
    ),
  },
  "documentation": {
    bg: "#f3e8ff",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#9333ea" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="14" height="18" rx="2" />
        <path d="M8 7h6M8 11h6M8 15h4" />
      </svg>
    ),
  },
};

function BellIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 1.5a3.5 3.5 0 013.5 3.5v2.25l1 1.25H1.5l1-1.25V5A3.5 3.5 0 016 1.5z" />
      <path d="M4.75 9.5a1.25 1.25 0 002.5 0" />
    </svg>
  );
}

function CalendarSmallIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#ff7a00" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="2" width="10" height="9" rx="1.5" />
      <path d="M1 5h10M3.5 1v2M8.5 1v2" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="16" cy="10" r="1.5" />
    </svg>
  );
}

function ListViewIcon({ active }: { active: boolean }) {
  const c = active ? "#0517f8" : "#a2aab8";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      <rect x="2" y="3" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 4h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="7" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 8h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="11" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 12h7.5" stroke={c} strokeWidth="1.4" />
    </svg>
  );
}

function TableViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="1.5" />
      <path d="M2 6h12M6 6v8" />
    </svg>
  );
}

function BoardViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="5" height="12" rx="1" />
      <rect x="9" y="2" width="5" height="7" rx="1" />
    </svg>
  );
}

function MapViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L2 4v10l4-2 4 2 4-2V2l-4 2-4-2z" />
      <path d="M6 2v10M10 4v10" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M4 8h8M6 12h4" />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: WOStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: cfg.bg, color: cfg.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function AvatarGroup({ assignees }: { assignees: { initials: string }[] }) {
  if (!assignees.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      {assignees.slice(0, 4).map((a, i) => (
        <div key={i} style={{
          width: 24, height: 24, borderRadius: "50%",
          background: "#e6e8fe", border: "2px solid white",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 700, color: "#0517f8",
          fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px",
          marginLeft: i > 0 ? -4 : 0, flexShrink: 0, zIndex: 4 - i,
          position: "relative",
        }}>
          {a.initials}
        </div>
      ))}
    </div>
  );
}

function DueDateBadge({ daysLeft }: { daysLeft: number }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 6px", borderRadius: 4, background: "#f3f4f6",
      fontSize: 12, fontWeight: 700, color: "#748094",
      fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px", whiteSpace: "nowrap", flexShrink: 0,
    }}>
      <BellIcon />
      {daysLeft} days left
    </div>
  );
}

function ScheduleDateBadge({ date }: { date: string }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 6px", borderRadius: 4, background: "#fff7f0",
      fontSize: 12, fontWeight: 600, color: "#ff7a00",
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap", flexShrink: 0,
    }}>
      <CalendarSmallIcon />
      {date}
    </div>
  );
}

function NotificationBadge({ count }: { count: number }) {
  return (
    <div style={{
      position: "absolute", top: 34, left: -4,
      background: "#ee0064", border: "2px solid white",
      borderRadius: 99, padding: "2px 6px",
      fontSize: 10, fontWeight: 700, color: "white",
      fontFamily: "var(--font-family-base)", lineHeight: "14px",
      minWidth: 16, textAlign: "center",
    }}>
      {count}
    </div>
  );
}

interface WOCardProps {
  wo: WorkOrder;
  hovered: boolean;
  onHover: (id: string | null) => void;
  onClick: () => void;
}

function WorkOrderCard({ wo, hovered, onHover, onClick }: WOCardProps) {
  const typeConfig = WO_TYPE_CONFIG[wo.type];
  return (
    <div
      style={{
        background: hovered ? "#f3f4f6" : "white",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 8, padding: 16,
        display: "flex", alignItems: "center", gap: 0,
        cursor: "pointer", transition: "background 80ms ease",
      }}
      onMouseEnter={() => onHover(wo.id)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      {/* Icon */}
      <div style={{ position: "relative", flexShrink: 0, marginRight: 16 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10,
          background: typeConfig.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {typeConfig.icon}
        </div>
        {wo.notificationCount && <NotificationBadge count={wo.notificationCount} />}
      </div>

      {/* Title & info */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <p style={{
          fontSize: 16, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.32px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
          margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {wo.title}
        </p>
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 12, fontWeight: 500, color: "#748094",
          fontFamily: "var(--font-family-base)", lineHeight: "16px",
        }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>#{wo.number}</span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wo.company}</span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wo.assetName}</span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wo.assetId}</span>
        </div>
      </div>

      {/* Right side metadata */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 16 }}>
        <AvatarGroup assignees={wo.assignees} />
        {wo.daysLeft !== undefined && <DueDateBadge daysLeft={wo.daysLeft} />}
        {wo.scheduledDate && <ScheduleDateBadge date={wo.scheduledDate} />}
        <StatusBadge status={wo.status} />
        <button
          style={{
            border: "none", background: "transparent", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: 0, borderRadius: 4, flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface WorkOrdersListPageProps {
  onOpenWorkOrder: (id: string) => void;
  onCreateNew: () => void;
}

const VIEW_OPTIONS: { key: ViewMode; label: string; Icon: React.FC<{ active: boolean }> }[] = [
  { key: "list",  label: "List",  Icon: ListViewIcon },
  { key: "table", label: "Table", Icon: TableViewIcon },
  { key: "board", label: "Board", Icon: BoardViewIcon },
  { key: "map",   label: "Map",   Icon: MapViewIcon },
];

export function WorkOrdersListPage({ onOpenWorkOrder, onCreateNew }: WorkOrdersListPageProps) {
  const [viewMode,    setViewMode]    = React.useState<ViewMode>("list");
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <PageHeader
        breadcrumbs={[{ label: "Work Orders" }]}
        searchAction={() => {}}
        primaryAction={{ label: "New Work Order", onClick: onCreateNew }}
      />

      {/* View & filter bar */}
      <div style={{
        height: 60, flexShrink: 0,
        background: "var(--color-bg-primary)",
        borderBottom: "1px solid var(--color-border-primary)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px",
      }}>
        {/* Pill view tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {VIEW_OPTIONS.map(({ key, label, Icon }) => {
            const active = viewMode === key;
            return (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 8px", borderRadius: 4,
                  border: "none", cursor: "pointer",
                  background: active ? "#e6e8fe" : "transparent",
                  fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                  color: active ? "var(--color-content-primary)" : "#a2aab8",
                  fontFamily: "var(--font-family-base)", transition: "all 100ms",
                }}
              >
                <Icon active={active} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Filters button */}
        <button style={{
          height: 34, padding: "0 12px",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 8, background: "var(--color-bg-primary)", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
          fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
          color: "var(--color-content-primary)",
        }}>
          <FilterIcon />
          Filters
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-primary)", padding: "16px 24px 24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MOCK_WORK_ORDERS.map((wo) => (
            <WorkOrderCard
              key={wo.id}
              wo={wo}
              hovered={hoveredCard === wo.id}
              onHover={setHoveredCard}
              onClick={() => onOpenWorkOrder(wo.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
